import React from "react";
import { PageQuery } from "../../../../utils/models/pageQuery";
import { Organization } from "../../../../core/models/entities/Organization";
import { Order, OrderStatus } from "../../../../core/models/entities/Order";
import { QueryChanger } from "../../../shared/hooks/usePageQuery";
import { TableData } from "../../../components/GenericTable/FunctionalList";
import { useNavigate } from "react-router-dom";
import { orderColumnsConfig } from "../functions/orderColumnsConfig";
import { ColumnSettings } from "../../../components/GenericTable/SubComponents/ListFrame";
import { OrderRowItem } from "../components/order-list-item.component";
import { GetOverallApprovalStatus } from "../../../../core/models/entities/BaseLogo";
import { ApprovalStatus } from "../../../../core/models/entities/Approval";
import { useOrderActionHandler } from "../../order-create/hooks/useOrderActionsHandler";
import { Profile } from "../../../../core/models/entities/Profile";
import {
  FilterChangeEvent,
  FilterRecipe,
} from "../../../components/GenericTable/SubComponents/TableHeader/ListHeader";
import { Role } from "../../../../core/models/entities/Role";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { enviroment } from "../../../../enviroment";
import { stripShowOnlyOwnedFilters } from "../functions/showOnlyOwnedFilter";

export function useFunctionalListOrdersViewModel(
  profile: Profile,
  lastPage: number,
  query: PageQuery,
  customers: Organization[],
  isPageLoading: boolean,
  vendors: Organization[] | undefined,
  queryChanger: QueryChanger,
  onViewRejectNote: (order: Order) => void,
  onSelectOrder: (order: Order) => void,
  orders?: Order[],
  // keepPreviousData indicators + paginator hover-prefetch, passed straight
  // through to FunctionalList (see logo-list for the pattern).
  isFetching?: boolean,
  isPreviousData?: boolean,
  onPrefetchPage?: (pageIndex: number) => void | Promise<void>,
  // Admin-only cancel of Awaiting payment rows; undefined = no button rendered.
  onCancelOrder?: (order: Order) => void,
  // Admin-only price adjustment of Awaiting payment rows; undefined = no button.
  onAdjustPrices?: (order: Order) => void,
): TableData<Order> {
  const { t } = useTranslation();
  const navigate = useNavigate();

  function handleGoToDetailsOrder(order: Order) {
    navigate(
      `/order/confirm?orderId=${order.id}&orgId=${order.organization.id}`,
    );
  }

  const { handlePlaceVerdict, isLoading } = useOrderActionHandler();

  const filters = buildOrderFilterRecipes(t, profile, customers);

  // Flag ordersOwnedFilterFixEnabled: the header's showOnlyOwned key (see
  // showOnlyOwnedFilter.ts) never reaches the query - this list has no
  // translation for it and the API answers 500. Belt and braces next to the
  // header no longer showing the checkbox here: a remembered/stale value in
  // the header's state is dropped too. Off = today's handler, the very same
  // function reference, so the key passes through exactly as before.
  const handleFilterChange = enviroment.ordersOwnedFilterFixEnabled
    ? (event: FilterChangeEvent) =>
        queryChanger.handleFilterChange({
          activeFilters: stripShowOnlyOwnedFilters(event.activeFilters),
        })
    : queryChanger.handleFilterChange;

  return {
    columns: orderColumnsConfig(t),
    tableTitle: t("orders"),
    data: orders,
    onPageSizeChange: queryChanger.handlePageSizeChange,
    onSearchQueryChange: queryChanger.handleSearchQueryChange,
    onNewPage: queryChanger.handleNewPage,
    onFilterChange: handleFilterChange,
    onNewSort: queryChanger.handleSortByField,
    onRowClick: onSelectOrder,
    getKey: getKey,
    isLoading: isPageLoading || isLoading,
    isFetching: isFetching,
    isPreviousData: isPreviousData,
    onPrefetchPage: onPrefetchPage,
    lastPage: lastPage,
    query: query,
    renderRow: (columns: ColumnSettings, order: Order) => (
      <OrderRowItem
        columns={columns}
        order={order}
        onPlaceVerdict={handlePlaceVerdict}
        onSeeRejectNote={onViewRejectNote}
        onEditDesign={(order) => {
          handleGoToDetailsOrder(order);
        }}
        onCancelOrder={onCancelOrder}
        onAdjustPrices={onAdjustPrices}
        isLoading={isLoading}
      />
    ),
    filters: filters,
  };
}

const getKey = (order: Order) => order.id;

function buildOrderFilterRecipes(
  t: TFunction,
  profile: Profile,
  customers?: Organization[],
) {
  const filters: FilterRecipe<any>[] = [];

  if (profile.userOrganizationInformations.role >= Role.Vendor && customers) {
    filters.push({
      options: customers,
      key: "organization.id",
      getOptionValue: (org) => org.id,
      getOptionDisplay: (org) => org.name,
      defaultTitle: t("customer"),
    } as FilterRecipe<Organization>);
  }

  filters.push({
    options: [
      OrderState.Shipped,
      OrderState.InProduction,
      // Stripe prepayment: parked orders must be findable. The enum value
      // ("Awaiting payment") matches the API's Orders.Status string exactly,
      // so the verbatim filterByValue hits the DB rows directly.
      OrderState.AwaitingPayment,
      OrderState.Rejected,
      OrderState.Pending,
      OrderState.Draft,
    ],
    key: "status",
    getOptionValue: (org) => org as string,
    getOptionDisplay: (org) => org as string,
    defaultTitle: t("status"),
  } as FilterRecipe<OrderState>);

  return filters;
}

export function getOrderState(order: Order): OrderState {
  if (order.status === OrderStatus.Draft) {
    return OrderState.Draft;
  }

  if (order.status === OrderStatus.Pending) {
    return OrderState.Pending;
  }

  if (order.status === OrderStatus.Rejected) {
    return OrderState.Rejected;
  }

  if (order.status === OrderStatus.InProduction) {
    return OrderState.InProduction;
  }

  if (order.status === OrderStatus.OrderShipped) {
    return OrderState.Shipped;
  }

  if (order.status === OrderStatus.AwaitingEntry) {
    return OrderState.Awaiting;
  }

  // Stripe-prepayment parked orders: must return before the approval
  // fall-through below - diverted orders have no approval group, and
  // GetOverallApprovalStatus dereferences it.
  if (order.status === OrderStatus.AwaitingPayment) {
    return OrderState.AwaitingPayment;
  }

  switch (GetOverallApprovalStatus(order.approval!)) {
    case ApprovalStatus.Rejected:
      return OrderState.Rejected;
    case ApprovalStatus.Approved:
      return OrderState.InProduction;
    case ApprovalStatus.Pending:
      return OrderState.Pending;
  }
}

export enum OrderState {
  Draft = "Draft",
  Awaiting = "Awaiting",
  AwaitingPayment = "Awaiting payment",
  Pending = "Pending",
  Rejected = "Rejected",
  InProduction = "In Production",
  Shipped = "Shipped",
}
